import { all, takeEvery, takeLatest, put, call, select } from 'redux-saga/effects';
import { getWorklistsItemCount } from '../worklists/slice';
import { selectClaim } from '../claim/selectors';
import {
  getWorklistClaims,
  getWorklistClaimsSucceeded,
  getWorklistClaimsFailed,
  getWorklistClaimMessages,
  getWorklistClaimMessagesSucceeded,
  getWorklistClaimMessagesFailed,
  getWorklistClaimUnreadSince,
  getWorklistClaimUnreadSinceSucceeded,
  getWorklistClaimUnreadSinceFailed,
  deleteWorklistClaimUnreadSince,
  deleteWorklistClaimUnreadSinceSucceeded,
  deleteWorklistClaimUnreadSinceFailed,
  getClaimMessageRecipientOrganizations,
  getClaimMessageRecipientOrganizationsSucceeded,
  getClaimMessageRecipientOrganizationsFailed,
  createWorklistClaimMessage,
  createWorklistClaimMessageSucceeded,
  createWorklistClaimMessageFailed,
  createWorklistClaimMessageAndConnectedActions,
  createWorklistClaimMessageAndConnectedActionsSucceeded,
  createWorklistClaimMessageAndConnectedActionsFailed,
  createWorklistClaimMessageAiRecommendation,
  createWorklistClaimMessageAiRecommendationSucceeded,
  createWorklistClaimMessageAiRecommendationFailed,
  createWorklistClaimMessageAiRecommendationUnavailable,
  updateWorklistClaimMessage,
  updateWorklistClaimMessageSucceeded,
  updateWorklistClaimMessageFailed,
  getWorklistClaimRecommendations,
  getWorklistClaimRecommendationsSucceeded,
  getWorklistClaimRecommendationsFailed,
  postWorklistClaimBipro,
  postWorklistClaimBiproSucceeded,
  postWorklistClaimBiproFailed,
  postWorklistClaimSettlementBipro,
  postWorklistClaimSettlementBiproSucceeded,
  postWorklistClaimSettlementBiproFailed,
  postWorklistClaimSettlementPropertyExpert,
  postWorklistClaimSettlementPropertyExpertSucceeded,
  postWorklistClaimSettlementPropertyExpertFailed,
  getWorklistClaimReminders,
  getWorklistClaimRemindersSucceeded,
  getWorklistClaimRemindersFailed,
  createWorklistClaimReminder,
  createWorklistClaimReminderSucceeded,
  createWorklistClaimReminderFailed,
  updateWorklistClaimReminder,
  updateWorklistClaimReminderSucceeded,
  updateWorklistClaimReminderFailed,
  deleteWorklistClaimReminder,
  deleteWorklistClaimReminderSucceeded,
  deleteWorklistClaimReminderFailed
} from './slice';
import {
  onGetWorklistClaims,
  onGetWorklistClaimMessages,
  onGetWorklistClaimUnreadSince,
  onDeleteWorklistClaimUnreadSince,
  onCreateWorklistClaimMessage,
  onCreateClaimMessageRecommendation,
  onUpdateWorklistClaimMessage,
  onGetClaimMessageRecipientOrganizations,
  onGetWorklistClaimRecommendations,
  onPostWorklistClaimBipro,
  onPostWorklistClaimSettlementBipro,
  onPostWorklistClaimSettlementPropertyExpert,
  onGetWorklistClaimReminders,
  onCreateWorklistClaimReminder,
  onUpdateWorklistClaimReminder,
  onDeleteWorklistClaimReminder
} from './api';

export function* getWorklistClaimsWorker({ payload }) {
  const { params } = payload || {};

  try {
    const response = yield call(onGetWorklistClaims, params);
    yield put(getWorklistClaimsSucceeded(response.data));
  } catch {
    yield put(getWorklistClaimsFailed());
  }
}

export function* getWorklistClaimMessagesWorker({ payload }) {
  const { claimId } = payload;

  try {
    const response = yield call(onGetWorklistClaimMessages, claimId);
    yield put(getWorklistClaimMessagesSucceeded(response.data));
  } catch {
    yield put(getWorklistClaimMessagesFailed());
  }
}

export function* getWorklistClaimUnreadSinceWorker({ payload }) {
  const { claimId } = payload;

  try {
    const response = yield call(onGetWorklistClaimUnreadSince, claimId);
    yield put(getWorklistClaimUnreadSinceSucceeded(response.data.unread_since));
  } catch {
    yield put(getWorklistClaimUnreadSinceFailed());
  }
}

export function* deleteWorklistClaimUnreadSinceWorker({ payload }) {
  const { claimId } = payload;

  try {
    yield call(onDeleteWorklistClaimUnreadSince, claimId);
    yield put(deleteWorklistClaimUnreadSinceSucceeded());
    yield put(getWorklistsItemCount());
    yield put(getWorklistClaimUnreadSince({ claimId }));
  } catch {
    yield put(deleteWorklistClaimUnreadSinceFailed());
  }
}

export function* getMessageRecipientOrganizationsWorker({ payload }) {
  const { claimId } = payload;

  try {
    const response = yield call(onGetClaimMessageRecipientOrganizations, claimId);
    yield put(getClaimMessageRecipientOrganizationsSucceeded(response.data));
  } catch {
    yield put(getClaimMessageRecipientOrganizationsFailed());
  }
}

export function* createWorklistClaimMessageWorker({ payload }) {
  const { claimId, message, params, callback } = payload;

  try {
    yield call(onCreateWorklistClaimMessage, claimId, message, params);
    yield put(createWorklistClaimMessageSucceeded());
    callback?.();
  } catch {
    yield put(createWorklistClaimMessageFailed());
  }
}

export function* createWorklistClaimMessageAndConnectedActionsWorker({ payload }) {
  const { createReminder, claimId, createMessageParams, newMessage, newReminder, callback } = payload;

  try {
    yield call(onCreateWorklistClaimMessage, claimId, newMessage, createMessageParams);

    if (createReminder) {
      yield call(onCreateWorklistClaimReminder, claimId, newReminder);
    }

    yield put(createWorklistClaimMessageAndConnectedActionsSucceeded());
    callback?.();
  } catch {
    yield put(createWorklistClaimMessageAndConnectedActionsFailed());
  }
}

export function* createWorklistClaimMessageAiRecommendationWorker({ payload }) {
  const { claimId, draftMessage, selectedRecipientIds, callback } = payload;

  try {
    const response = yield call(onCreateClaimMessageRecommendation, claimId, {
      draft_message: draftMessage,
      selected_recipient_ids: selectedRecipientIds
    });
    yield put(createWorklistClaimMessageAiRecommendationSucceeded(response.data));
    callback?.(response.data);
  } catch (error) {
    if (error?.response?.status === 503 && error?.response?.data?.error_code === 'ai_unavailable') {
      yield put(createWorklistClaimMessageAiRecommendationUnavailable());
    } else {
      yield put(createWorklistClaimMessageAiRecommendationFailed());
    }
  }
}

export function* updateWorklistClaimMessageWorker({ payload }) {
  const { message, callback } = payload;

  try {
    yield call(onUpdateWorklistClaimMessage, message);
    yield put(updateWorklistClaimMessageSucceeded());
    callback?.();
  } catch {
    yield put(updateWorklistClaimMessageFailed());
  }
}

export function* postWorklistClaimBiproWorker({ payload }) {
  const { claimId } = payload;

  try {
    yield call(onPostWorklistClaimBipro, claimId);
    yield put(postWorklistClaimBiproSucceeded());
  } catch {
    yield put(postWorklistClaimBiproFailed());
  }
}

export function* postWorklistClaimSettlementBiproWorker({ payload }) {
  const { params } = payload;

  try {
    yield call(onPostWorklistClaimSettlementBipro, params);
    yield put(postWorklistClaimSettlementBiproSucceeded());
  } catch {
    yield put(postWorklistClaimSettlementBiproFailed());
  }
}

export function* postWorklistClaimSettlementPropertyExpertWorker({ payload }) {
  const { params } = payload;

  try {
    yield call(onPostWorklistClaimSettlementPropertyExpert, params);
    yield put(postWorklistClaimSettlementPropertyExpertSucceeded());
  } catch {
    yield put(postWorklistClaimSettlementPropertyExpertFailed());
  }
}

export function* getWorklistClaimRecommendationsWorker({ payload }) {
  const { claimId } = payload;

  try {
    const response = yield call(onGetWorklistClaimRecommendations, claimId);
    yield put(getWorklistClaimRecommendationsSucceeded(response.data));
  } catch {
    yield put(getWorklistClaimRecommendationsFailed());
  }
}

export function* getWorklistClaimRemindersWorker({ payload }) {
  const { claimId } = payload;

  try {
    const response = yield call(onGetWorklistClaimReminders, claimId);
    yield put(getWorklistClaimRemindersSucceeded(response.data));
  } catch {
    yield put(getWorklistClaimRemindersFailed());
  }
}

export function* createWorklistClaimReminderWorker({ payload }) {
  const { claimId, reminder, callback } = payload;

  try {
    yield call(onCreateWorklistClaimReminder, claimId, reminder);
    yield put(createWorklistClaimReminderSucceeded());
    callback?.();
  } catch {
    yield put(createWorklistClaimReminderFailed());
  }
}

export function* updateWorklistClaimReminderWorker({ payload }) {
  const { reminderId, reminder, callback } = payload;

  try {
    yield call(onUpdateWorklistClaimReminder, reminderId, reminder);
    yield put(updateWorklistClaimReminderSucceeded());
    callback?.();
  } catch {
    yield put(updateWorklistClaimReminderFailed());
  }
}

export function* deleteWorklistClaimReminderWorker({ payload }) {
  const { reminderId, callback } = payload;

  try {
    yield call(onDeleteWorklistClaimReminder, reminderId);
    yield put(deleteWorklistClaimReminderSucceeded());
    yield put(getWorklistsItemCount());
    callback?.();
  } catch {
    yield put(deleteWorklistClaimReminderFailed());
  }
}

function* reloadWorklistClaimAllData() {
  const claim = yield select(selectClaim);
  const claimId = claim?.id;

  if (claim) {
    yield all([
      put(getWorklistClaimMessages({ claimId })),
      put(getWorklistClaimRecommendations({ claimId })),
      put(getWorklistClaimReminders({ claimId })),
      put(getWorklistClaimUnreadSince({ claimId }))
    ]);
    yield put(getWorklistsItemCount());
  }
}

export default function* WorklistClaimsSaga() {
  yield all([
    takeLatest(getWorklistClaims.type, getWorklistClaimsWorker),
    takeLatest(getWorklistClaimMessages.type, getWorklistClaimMessagesWorker),
    takeLatest(getWorklistClaimUnreadSince.type, getWorklistClaimUnreadSinceWorker),
    takeLatest(getClaimMessageRecipientOrganizations.type, getMessageRecipientOrganizationsWorker),
    takeLatest(getWorklistClaimRecommendations.type, getWorklistClaimRecommendationsWorker),
    takeLatest(getWorklistClaimReminders.type, getWorklistClaimRemindersWorker),
    takeLatest(deleteWorklistClaimUnreadSince.type, deleteWorklistClaimUnreadSinceWorker),
    takeLatest(createWorklistClaimMessage.type, createWorklistClaimMessageWorker),
    takeLatest(createWorklistClaimMessageAndConnectedActions.type, createWorklistClaimMessageAndConnectedActionsWorker),
    takeLatest(updateWorklistClaimMessage.type, updateWorklistClaimMessageWorker),
    takeLatest(postWorklistClaimBipro.type, postWorklistClaimBiproWorker),
    takeLatest(postWorklistClaimSettlementBipro.type, postWorklistClaimSettlementBiproWorker),
    takeLatest(postWorklistClaimSettlementPropertyExpert.type, postWorklistClaimSettlementPropertyExpertWorker),
    takeLatest(createWorklistClaimReminder.type, createWorklistClaimReminderWorker),
    takeLatest(updateWorklistClaimReminder.type, updateWorklistClaimReminderWorker),
    takeLatest(deleteWorklistClaimReminder.type, deleteWorklistClaimReminderWorker),
    takeLatest(createWorklistClaimMessageAiRecommendation.type, createWorklistClaimMessageAiRecommendationWorker),
    takeEvery(createWorklistClaimMessageSucceeded.type, reloadWorklistClaimAllData),
    takeEvery(createWorklistClaimMessageAndConnectedActionsSucceeded.type, reloadWorklistClaimAllData),
    takeEvery(updateWorklistClaimMessageSucceeded.type, reloadWorklistClaimAllData),
    takeEvery(postWorklistClaimBiproSucceeded.type, reloadWorklistClaimAllData),
    takeEvery(postWorklistClaimSettlementBiproSucceeded.type, reloadWorklistClaimAllData),
    takeEvery(postWorklistClaimSettlementPropertyExpertSucceeded.type, reloadWorklistClaimAllData),
    takeEvery(createWorklistClaimReminderSucceeded.type, reloadWorklistClaimAllData),
    takeEvery(updateWorklistClaimReminderSucceeded.type, reloadWorklistClaimAllData),
    takeEvery(deleteWorklistClaimReminderSucceeded.type, reloadWorklistClaimAllData)
  ]);
}
